import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopNavigation() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-24 bg-white/10 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-xs font-semibold tracking-[0.1em] uppercase text-white/90 hover:text-white transition-colors py-2 block">
                {item.label}
                <span className="absolute left-0 bottom-0 h-[1px] w-0 bg-white/80 transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavigation({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 bg-[#0A0A0A] pt-20 px-6 lg:hidden flex flex-col gap-6 animate-in fade-in slide-in-from-top-5 duration-300">
      <nav className="flex flex-col gap-6">
        {loading ? <div className="flex flex-col gap-6">
            {[1, 2, 3].map(i => <div key={i} className="h-6 w-3/4 bg-white/10 animate-pulse rounded" />)}
          </div> : menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-white/90 border-b border-white/10 pb-4 uppercase tracking-widest hover:text-white transition-colors" onClick={onClose}>
              {item.label}
            </Link>)}
        <Button asChild className="mt-4 w-full border border-white/30 bg-transparent text-white hover:bg-white hover:text-black transition-all uppercase tracking-widest text-xs h-11">
          <Link to="/ContactPage" onClick={onClose}>
            Request Capital
          </Link>
        </Button>
      </nav>
    </div>;
}
export default function GlobalHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="global-header" className="fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500 border-b dark text-foreground bg-[#0A0A0A] border-transparent !h-[85px]">
      <div className="container mx-auto px-6 flex items-center justify-between h-full">
        <Link to="/" className="relative z-50 flex-shrink-0">
          <img src="https://dynestate.com/wp-content/uploads/2026/03/dynestate_logo_transparent-e1772856656558.png" alt="Dynestate" className="object-contain !w-[150px] !h-[150px]" />
        </Link>

        <div className="hidden lg:flex flex-1 justify-center px-8">
          <MenuProvider menu_id="44">
            <DesktopNavigation />
          </MenuProvider>
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <Button asChild variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-black transition-all uppercase tracking-[0.1em] text-[11px] font-semibold h-9 px-6 rounded-sm">
            <Link to="/ContactPage">Request Capital</Link>
          </Button>
        </div>

        <Button variant="ghost" size="icon" className="lg:hidden relative z-50 text-white hover:bg-white/10 hover:text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      <MenuProvider menu_id="44">
        <MobileNavigation isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}